import { formatCents } from '@/pages/collabs/brief-templates/_shared/format-money';
import {
  CollabType,
  CreatorHubSystemEventName,
  collabDashboardHref,
  type ConversationCollabBriefAcceptedData,
  type ConversationCollabPaymentDueData,
  type ConversationGiftMarkerData,
  type DashboardSystemEventFeedItem,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ApplicationEventCard from './ApplicationEventCard';
import MentionEventCard from './MentionEventCard';
import SystemEventCard from './SystemEventCard';
import { getSystemEventGlyph, isGiftMarker } from './system-event-presentation';
import { useSystemEventLabel } from './use-system-event-label';
import { collabTrackingHref } from '@/pages/collabs/suivi/collab-tracking-href';

interface SystemEventEntryProps {
  event: DashboardSystemEventFeedItem;
  /** The creator of the thread, whose first name an application marker says. */
  participantName?: string | null;
  /**
   * Where "See the application" leads. The applications drawer hands it back to
   * its own Application tab; anywhere else it opens the applications page.
   */
  onOpenApplication?: (applicationId: string) => void;
}

/** Where the brand reads an application: the applications page, drawer open. */
const applicationHref = (applicationId: string): string =>
  `/applications?c=${encodeURIComponent(applicationId)}`;

/**
 * Where the brand reads a collab: the page of its type, from the type the
 * marker froze. A marker written before the type was carried opens the
 * sponsored tracking page, the only type that existed then.
 */
const collabHref = (collab: { collabId: string; collabType?: string | null }): string =>
  collab.collabType
    ? collabDashboardHref({ id: collab.collabId, collabType: collab.collabType })
    : collabTrackingHref(collab.collabId);

/**
 * The collab a marker names, denormalized in its own payload. Nothing here
 * resolves a collab per feed item: the thread is a register, not a view.
 */
const collabReference = (
  event: DashboardSystemEventFeedItem
): ConversationCollabBriefAcceptedData | ConversationCollabPaymentDueData | null =>
  event.event === CreatorHubSystemEventName.COLLAB_BRIEF_ACCEPTED ||
  event.event === CreatorHubSystemEventName.COLLAB_PAYMENT_DUE ||
  event.event === CreatorHubSystemEventName.COLLAB_PROPOSED
    ? event.data
    : null;

/** The gift a marker names, denormalized in its own payload like a collab marker. */
const giftReference = (event: DashboardSystemEventFeedItem): ConversationGiftMarkerData | null =>
  isGiftMarker(event.event) ? (event.data as ConversationGiftMarkerData) : null;

/** The values line of a gift: the size once claimed, the parcel once shipped. */
const giftDetails = (event: string, gift: ConversationGiftMarkerData): string | undefined => {
  if (event === CreatorHubSystemEventName.GIFT_CLAIMED) return gift.variantTitle ?? undefined;
  if (event === CreatorHubSystemEventName.GIFT_SHIPPED) {
    const parcel = [gift.carrier, gift.trackingNumber].filter(Boolean).join(' · ');
    return parcel || undefined;
  }
  return undefined;
};

/** A shipped parcel opens its carrier's tracking page, outside the dashboard. */
const openTracking = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

/**
 * Picks the registry card a marker deserves. Only the mention marker has a
 * variant of its own; every other name — known or not — takes the plain card.
 */
const SystemEventEntry = ({ event, participantName, onOpenApplication }: SystemEventEntryProps) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    collabMarkerTitle,
    giftMarkerTitle,
    systemEventTitle,
    receptionQuestionTitle,
    receptionCountedTitle,
    receptionAnswerLabel,
  } = useSystemEventLabel();

  if (
    event.event === CreatorHubSystemEventName.BRIEF_APPLICATION_SUBMITTED ||
    event.event === CreatorHubSystemEventName.PROGRAM_APPLICATION_SUBMITTED
  ) {
    const { applicationId } = event.data;
    const brief = event.event === CreatorHubSystemEventName.BRIEF_APPLICATION_SUBMITTED;
    const title = brief ? event.data.title : event.data.programName;
    return (
      <ApplicationEventCard
        kind={brief ? 'brief' : 'program'}
        creatorName={participantName ?? null}
        targetTitle={title}
        occurredAt={event.occurredAt}
        onOpen={() =>
          onOpenApplication
            ? onOpenApplication(applicationId)
            : navigate(applicationHref(applicationId))
        }
      />
    );
  }

  if (event.event === CreatorHubSystemEventName.MENTION_RECEIVED) {
    return (
      <MentionEventCard
        mentionId={event.data.mentionId}
        mentionType={event.data.mentionType}
        occurredAt={event.occurredAt}
      />
    );
  }

  const Glyph = getSystemEventGlyph(event.event);
  const gift = giftReference(event);

  // Asked of the creator, answered by her: the brand reads both and answers nothing.
  if (event.event === CreatorHubSystemEventName.PRODUCT_RECEPTION_QUESTION) {
    return (
      <SystemEventCard
        icon={<Glyph width={13} height={13} />}
        title={receptionQuestionTitle(event.data.productName)}
        details={receptionAnswerLabel(event.data.answer, event.data.closedBy ?? null)}
        occurredAt={event.occurredAt}
        // The question only exists on a UGC collab, which the UGC page follows.
        onOpen={() =>
          navigate(collabDashboardHref({ id: event.data.collabId, collabType: CollabType.UGC }))
        }
      />
    );
  }

  if (event.event === CreatorHubSystemEventName.PRODUCT_RECEPTION_COUNTED) {
    return (
      <SystemEventCard
        icon={<Glyph width={13} height={13} />}
        title={receptionCountedTitle(event.data.receivedAt, event.data.productName)}
        occurredAt={event.occurredAt}
        onOpen={() =>
          navigate(collabDashboardHref({ id: event.data.collabId, collabType: CollabType.UGC }))
        }
      />
    );
  }

  if (gift) {
    const trackingUrl =
      event.event === CreatorHubSystemEventName.GIFT_SHIPPED ? gift.trackingUrl : null;

    return (
      <SystemEventCard
        icon={<Glyph width={13} height={13} />}
        title={giftMarkerTitle(event.event, gift.productTitle)}
        details={giftDetails(event.event, gift)}
        occurredAt={event.occurredAt}
        onOpen={trackingUrl ? () => openTracking(trackingUrl) : undefined}
      />
    );
  }

  const collab = collabReference(event);

  const details =
    event.event === CreatorHubSystemEventName.COLLAB_PAYMENT_DUE
      ? formatCents(event.data.amountCents, event.data.currency, i18n.language)
      : undefined;

  return (
    <SystemEventCard
      icon={<Glyph width={13} height={13} />}
      title={
        collab ? collabMarkerTitle(event.event, collab.collabName) : systemEventTitle(event.event)
      }
      details={details}
      occurredAt={event.occurredAt}
      onOpen={collab ? () => navigate(collabHref(collab)) : undefined}
    />
  );
};

export default SystemEventEntry;
